import { SystemConstant, AppConstant, KeyConstant } from "const";
import { getInteractor, getSemaphore } from "services/local.service";
import store, { SystemActions } from "redux-store";
import { convertString2JSON, uuid, toSnake, isE2E, toCamel, isEncryption, StorageUtil, isJSONString } from "utils";
import { sendOneMessageSenderKeyDistributionMessage } from "pubsub/services/message/error-message";
import { saveMessageInQueue } from "utils/message.utils";
import { isSupportResendE2EDevice } from "utils";

const MAX_RETRY_ERROR = 3;
const MAX_RETRY_ERROR_ONE_MESSAGE = 5;
const MAX_RESEND_SENDER = 3;
const MAX_RESEND_MESSAGE = 200;
const MAX_RETRY_TIMES = 30;
const getEncryptTypeName = window.bglobalSignal.libs.EncryptTypeProtocol.getEncryptTypeName;
const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;

class EncryptStatus {
  resolveEncryptStatus = async (message, group, prefixKey) => {
    if (message.sendType !== SystemConstant.SEND_TYPE.encryptStatus) {
      throw new Error("Wrong send type");
    }
    const messageEncryptStatusContentJson = message.content ? convertString2JSON(message.content, {}) : {};
    const encryptStatusType = messageEncryptStatusContentJson?.encrypt_status_type;
    let result = {};
    switch (true) {
      case encryptStatusType === SystemConstant.ENCRYPT_STATUS_TYPE.error &&
        messageEncryptStatusContentJson.waiting_index < messageEncryptStatusContentJson?.last_index:
        result = await this.processResendEncryptStatus(message, group, prefixKey, messageEncryptStatusContentJson);
        break;

      case encryptStatusType === SystemConstant.ENCRYPT_STATUS_TYPE.success:
        result = await this.processSuccessEncryptStatus(message, group, prefixKey, messageEncryptStatusContentJson);
        break;

      default:
        result = await this.processLostErrorEncryptStatus(message, group, prefixKey, messageEncryptStatusContentJson);
        break;
    }
    await getInteractor(prefixKey).LocalMessageService.saveFromRemote([toSnake(message)]);
    return result;
  };

  processResendEncryptStatus = async (message, group, prefixKey, messageEncryptStatusContentJson) => {
    if (group.groupType === SystemConstant.GROUP_CHAT_TYPE.personal) {
      let msgEncrypt = await getInteractor(prefixKey).LocalMessageEncryptService.findOneBySendToIndexSendNumber(
        message.accountId,
        message.deviceId,
        message.senderId,
        message.senderDeviceId,
        messageEncryptStatusContentJson.waiting_index,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
        SystemConstant.SESSION_TYPE.session,
      );
      if (!message) return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
      let messageResend = await getInteractor(prefixKey).LocalMessageService.findOne({ id: msgEncrypt.id });
      if (!messageResend) return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
      let limit = MAX_RETRY_TIMES * 2;
      let messageResendCount = await getInteractor(prefixKey).LocalMessageService.count(
        { source_id: msgEncrypt.source_id },
        ` LIMIT ${limit} `,
      );
      if (messageResendCount > MAX_RETRY_TIMES) {
        let messageLastResend = await getInteractor(prefixKey).LocalMessageService.findOne(
          { source_id: msgEncrypt.source_id },
          " ORDER BY CREATED DESC ",
        );
        if (
          !messageLastResend ||
          (messageLastResend &&
            messageLastResend.created > Date.now() - (messageResendCount - MAX_RETRY_TIMES) * 300000)
        ) {
          return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
        }
        if (messageResendCount >= limit) {
          return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
        }
      }
      let messageOptionsObj = isJSONString(messageResend.options)
        ? convertString2JSON(messageResend.options, {})
        : messageResend.options;
      delete messageOptionsObj.index_send_number;
      delete messageOptionsObj.prev_send_number;

      const mentions = isJSONString(messageResend.options)
        ? convertString2JSON(messageResend.mentions, [])
        : messageResend.mentions;
      messageResend.id = uuid();
      await saveMessageInQueue({
        prefixKey,
        groupId: messageResend.group_id,
        threadId: messageResend.thread_id,
        content: messageResend.content,
        parentId: messageResend.parent_id,
        mentionIds: mentions,
        sendType: messageResend.send_type,
        options: messageOptionsObj,
        sourceId: messageResend.source_id,
      });
      return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
    } else {
      if (messageEncryptStatusContentJson.is_reset_session === 1) {
        await sendOneMessageSenderKeyDistributionMessage(
          prefixKey,
          group,
          message,
          messageEncryptStatusContentJson.encrypt_version,
        );
      }
      if (messageEncryptStatusContentJson.decrypt_send_type === SystemConstant.SEND_TYPE.senderKey)
        return {
          type: SystemConstant.MESSAGE_ERROR_TYPE.NONE,
          message: "OK",
        };

      /// Ignore to resendE2E new message for not supported device
      const responseStatusDevice = await getInteractor(prefixKey).LocalDeviceService.get(message.senderDeviceId);
      if (responseStatusDevice == null) return null;
      if (!isSupportResendE2EDevice(responseStatusDevice)) {
        return null;
      }

      let msgEncrypts = await getInteractor(prefixKey).LocalMessageEncryptService.findListBySendToIndexSendNumber(
        message.accountId,
        message.deviceId,
        messageEncryptStatusContentJson.waiting_index,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
        SystemConstant.SESSION_TYPE.sender,
      );
      if (msgEncrypts.length === 0) return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
      let messageResend = await getInteractor(prefixKey).LocalMessageService.findOne({ id: msgEncrypts[0].id });
      if (!messageResend) return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
      let limit = MAX_RETRY_TIMES * 10;
      let messageResendCount = await getInteractor(prefixKey).LocalMessageService.count(
        { source_id: msgEncrypts[0].source_id },
        ` LIMIT ${limit} `,
      );
      if (messageResendCount > MAX_RETRY_TIMES) {
        let messageLastResend = await getInteractor(prefixKey).LocalMessageService.findOne(
          { source_id: msgEncrypts[0].source_id },
          " ORDER BY CREATED DESC ",
        );
        if (
          !messageLastResend ||
          (messageLastResend && messageLastResend.created > Date.now() - (messageResendCount - MAX_RETRY_TIMES) * 1000)
        ) {
          return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
        }
        if (messageResendCount >= limit) {
          return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
        }
      }

      // Resend 1 on 1
      messageResend.id = uuid();
      messageResend.device_id = message.senderDeviceId;
      this.resendOneOnOne({
        encryptStatusMes: message,
        resendMessages: [messageResend],
        sendWaitingIndex: null,
        prefixKey: prefixKey,
        encryptTypeProtocol: messageEncryptStatusContentJson.encrypt_version,
        isResendE2E: false,
      });
      return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
    }
  };

  processLostErrorEncryptStatus = async (message, group, prefixKey, messageEncryptStatusContentJson) => {
    const decryptSendType = messageEncryptStatusContentJson?.decrypt_send_type;
    if (isE2E(decryptSendType, group.groupType)) {
      return await this.processInboxErrorEncryptStatus(message, group, prefixKey, messageEncryptStatusContentJson);
    } else {
      return await this.processGroupErrorEncryptStatus(message, group, prefixKey, messageEncryptStatusContentJson);
    }
  };

  processSuccessEncryptStatus = async (message, group, prefixKey, messageEncryptStatusContentJson) => {
    const decryptSendType = messageEncryptStatusContentJson?.decrypt_send_type;
    if (isE2E(decryptSendType, group.groupType)) {
      if (group.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal) {
        await getInteractor(prefixKey).LocalSenderKeySharedService.save([
          {
            group_id: group.id,
            account_id: message.senderId,
            device_id: message.senderDeviceId,
            encryption_type_protocol: getEncryptTypeName(messageEncryptStatusContentJson.encrypt_version),
          },
        ]);
      }
      return await this.processInboxSuccessEncryptStatus(message, prefixKey, messageEncryptStatusContentJson);
    } else {
      return await this.processGroupSuccessEncryptStatus(message, prefixKey, messageEncryptStatusContentJson);
    }
  };

  processInboxErrorEncryptStatus = async (message, group, prefixKey, messageEncryptStatusContentJson) => {
    const isResetSession = StorageUtil.getCommonKey(KeyConstant.KEY_RESET_SESSION);

    let waitingIndex = messageEncryptStatusContentJson.waiting_index;
    let session = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
      message.senderId,
      message.senderDeviceId,
      message.groupId,
      messageEncryptStatusContentJson.encrypt_version,
    );

    if (session == null && group.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal) {
      const encryptTypeProtocol = findEncryptTypeProtocol(messageEncryptStatusContentJson.encrypt_version);
      await sendOneMessageSenderKeyDistributionMessage(prefixKey, group, message, encryptTypeProtocol);
    }
    if (session == null) return;
    if (waitingIndex > session?.success_index || waitingIndex === -1) {
      let msgEncrypt;
      if (waitingIndex !== -1) {
        msgEncrypt = await getInteractor(prefixKey).LocalMessageEncryptService.findOneBySendToIndexSendNumber(
          message.accountId,
          message.deviceId,
          message.senderId,
          message.senderDeviceId,
          waitingIndex,
          message.groupId,
          messageEncryptStatusContentJson.encrypt_version,
          SystemConstant.SESSION_TYPE.session,
        );
      } else {
        msgEncrypt = null;
      }
      let isSender = await getInteractor(prefixKey).LocalCryptoService.getSessionIsSender(
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
      );
      if (
        session.retry_error < MAX_RETRY_ERROR &&
        (msgEncrypt || waitingIndex === -1) &&
        !isResetSession &&
        messageEncryptStatusContentJson.encrypt_status_type !== SystemConstant.ENCRYPT_STATUS_TYPE.resetSession
      ) {
        const updateValues = {
          retry_error: session.retry_error + 1,
          state: 1,
        };
        if (waitingIndex > session.error_index) updateValues.error_index = waitingIndex;
        await getInteractor(prefixKey).LocalCryptoService.updateSession(
          updateValues,
          message.senderId,
          message.senderDeviceId,
          message.groupId,
          messageEncryptStatusContentJson.encrypt_version,
        );
        if (waitingIndex === -1 && group.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal)
          return { type: SystemConstant.MESSAGE_ERROR_TYPE.RESET_SENDER_KEY_ERROR, message: "Sender key not found" };
        if (msgEncrypt) {
          msgEncrypt.resent_count += 1;
          const updateMessage = {
            resent_count: msgEncrypt.resent_count,
          };
          await getInteractor(prefixKey).LocalMessageEncryptService.update(updateMessage, { id: msgEncrypt.id });

          await this.sendToOneDeviceMessage(
            message,
            msgEncrypt,
            group,
            messageEncryptStatusContentJson.waiting_index,
            prefixKey,
          );
        }
      } else if (
        messageEncryptStatusContentJson.encrypt_status_type === SystemConstant.ENCRYPT_STATUS_TYPE.resetSession ||
        isSender
      ) {
        if (isResetSession) StorageUtil.setCommonKey(KeyConstant.KEY_RESET_SESSION, false);
        const updateValues = {
          retry_error: 1,
          state: 1,
        };
        if (waitingIndex > session.error_index) updateValues.error_index = waitingIndex;
        await getInteractor(prefixKey).LocalCryptoService.updateSession(
          updateValues,
          message.senderId,
          message.senderDeviceId,
          message.groupId,
          messageEncryptStatusContentJson.encrypt_version,
        );
        return await this.#resendResetIndex(
          prefixKey,
          group,
          session,
          message,
          waitingIndex,
          messageEncryptStatusContentJson,
        );
      }
    }
    return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
  };

  /**
   *
   * @param {string} prefixKey
   * @param {Object!} group
   * @param {Object!} session
   * @param {Object!} message
   * @param {number} waitingIndex
   * @param {string} messageEncryptStatusContentJson
   * @returns {Promise<{type: string, message: string}>}
   */
  #resendResetIndex = async (prefixKey, group, session, message, waitingIndex, messageEncryptStatusContentJson) => {
    //resend old with new index
    let newWaitingIndex = session.index_number + 1000;
    if (group.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal) {
      await getInteractor(prefixKey).LocalCryptoService.deleteSession(
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
      );
      return {
        type: SystemConstant.MESSAGE_ERROR_TYPE.RESET_SENDER_KEY_ERROR,
        message: "Sender key not found",
        newIndexSend: newWaitingIndex,
      };
    } else {
      const reSendMessageInbox = await getInteractor(prefixKey).LocalMessageService.getReSendMessageInbox(
        waitingIndex,
        message.deviceId,
        message.senderDeviceId,
        message.groupId,
      );
      const oldMessages = reSendMessageInbox.reduce((arr, oldMessage) => {
        const options = convertString2JSON(oldMessage.options, {});
        if (
          isEncryption(oldMessage.send_type) === SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION &&
          options?.encryption_f !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION &&
          !arr.some(msg => msg.source_id === oldMessage.source_id)
        ) {
          arr.push(oldMessage);
        }
        return arr;
      }, []);
      let resendMessages = oldMessages.slice(0, Math.min(oldMessages.size, MAX_RESEND_MESSAGE)).reverse();

      await this.resendOneOnOne({
        resendMessages: resendMessages,
        sendWaitingIndex: newWaitingIndex,
        prefixKey: prefixKey,
        encryptTypeProtocol: messageEncryptStatusContentJson.encrypt_version,
        isResendE2E: false,
      });
    }
    return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
  };

  /**
   *
   * @param message
   * @param group
   * @param {string} prefixKey
   * @param messageEncryptStatusContentJson
   * @returns {Promise<{type: string, message: string}>}
   */
  processGroupErrorEncryptStatus = async (message, group, prefixKey, messageEncryptStatusContentJson) => {
    const isResetSession = StorageUtil.getCommonKey(KeyConstant.KEY_RESET_SESSION);
    let waitingIndex = messageEncryptStatusContentJson.waiting_index;
    let senderKey = toCamel(
      await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
      ),
    );
    if (!senderKey || Object.keys(senderKey).length === 0)
      return {
        type: SystemConstant.MESSAGE_ERROR_TYPE.NONE,
        message: "OK",
      };
    if (waitingIndex > (senderKey?.successIndex || 0) || waitingIndex === -1) {
      //get failed message
      let msgEncrypt = await getInteractor(prefixKey).LocalMessageEncryptService.findOneBySenderIndexSendNumber(
        message.accountId,
        message.deviceId,
        waitingIndex,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
        SystemConstant.SESSION_TYPE.sender,
        true,
      );

      if (
        senderKey.retryError < AppConstant.MAX_RETRY_ERROR &&
        msgEncrypt != null &&
        msgEncrypt?.resent_count < MAX_RETRY_ERROR_ONE_MESSAGE &&
        !isResetSession
      ) {
        const updateValues = {
          retry_error: senderKey.retryError + 1,
          state: 1,
        };
        if (waitingIndex > (senderKey.errorIndex || 0)) updateValues.error_index = waitingIndex;
        await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
          updateValues,
          message.senderId,
          message.senderDeviceId,
          message.groupId,
          messageEncryptStatusContentJson.encrypt_version,
        );
        msgEncrypt.resent_count += 1;
        const updateMessage = {
          resent_count: msgEncrypt.resent_count,
          modified: new Date().getTime(),
        };
        await getInteractor(prefixKey).LocalMessageEncryptService.update(updateMessage, { id: msgEncrypt.id });

        await this.sendToOneDeviceMessage(
          message,
          msgEncrypt,
          group,
          messageEncryptStatusContentJson.waiting_index,
          prefixKey,
        );
      } else {
        if (isResetSession) StorageUtil.setCommonKey(KeyConstant.KEY_RESET_SESSION, false);
        let msgEncryptSenderKey = await getInteractor(
          prefixKey,
        ).LocalMessageEncryptService.findLastBySendToIndexSendNumber(
          message.accountId,
          message.deviceId,
          message.senderId,
          message.senderDeviceId,
          message.groupId,
          messageEncryptStatusContentJson.encrypt_version,
          SystemConstant.SESSION_TYPE.session,
        );
        if (msgEncryptSenderKey == null || msgEncryptSenderKey.resent_count >= MAX_RESEND_SENDER) {
          const updateValues = {
            retry_error: 1,
            state: 1,
          };
          if (waitingIndex > senderKey.errorIndex) updateValues.error_index = waitingIndex;
          await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
            updateValues,
            message.senderId,
            message.senderDeviceId,
            message.groupId,
            messageEncryptStatusContentJson.encrypt_version,
          );
          return { type: SystemConstant.MESSAGE_ERROR_TYPE.RESET_SENDER_KEY_ERROR, message: "Sender key not found" };
        } else {
          const updateValues = {
            retry_error: senderKey.retryError + 1,
            state: 1,
          };
          if (waitingIndex > senderKey.errorIndex) updateValues.error_index = waitingIndex;
          await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
            updateValues,
            message.senderId,
            message.senderDeviceId,
            message.groupId,
            messageEncryptStatusContentJson.encrypt_version,
          );

          msgEncryptSenderKey.resent_count += 1;
          const updateMessage = {
            resent_count: msgEncryptSenderKey.resent_count,
            modified: new Date().getTime(),
          };
          await getInteractor(prefixKey).LocalMessageEncryptService.update(updateMessage, {
            id: msgEncryptSenderKey.id,
          });

          await this.sendToOneDeviceMessage(
            message,
            msgEncryptSenderKey,
            group,
            messageEncryptStatusContentJson.waiting_index,
            prefixKey,
          );

          const reSendMessageGroup = await getInteractor(prefixKey).LocalMessageService.getReSendMessageGroup(
            waitingIndex,
            message.deviceId,
            message.groupId,
          );
          const oldMessages = reSendMessageGroup.reduce((arr, oldMessage) => {
            const options = convertString2JSON(oldMessage.options, {});
            if (
              isEncryption(oldMessage.send_type) === SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION &&
              options?.encryption_f !== SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION &&
              !arr.some(msg => msg.source_id === oldMessage.source_id)
            ) {
              arr.push(oldMessage);
            }
            return arr;
          }, []);

          const lastMessage = oldMessages.length > 0 ? oldMessages[oldMessages.length - 1] : null;
          if (lastMessage) {
            let msgOneEncrypt = await getInteractor(
              prefixKey,
            ).LocalMessageEncryptService.findOneBySenderIndexSendNumber(
              lastMessage.account_id,
              lastMessage.device_id,
              waitingIndex,
              lastMessage.group_id,
              messageEncryptStatusContentJson.encrypt_version,
              SystemConstant.SESSION_TYPE.sender,
              false,
            );
            msgOneEncrypt.resent_count += 1;
            const updateMessage = {
              resent_count: msgOneEncrypt.resent_count,
              modified: new Date().getTime(),
            };
            await getInteractor(prefixKey).LocalMessageEncryptService.update(updateMessage, {
              id: msgOneEncrypt.id,
            });
            await this.sendToOneDeviceMessage(
              message,
              msgOneEncrypt,
              group,
              messageEncryptStatusContentJson.waiting_index,
              prefixKey,
            );
          }
        }
      }
    }
    return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
  };

  processInboxSuccessEncryptStatus = async (message, prefixKey, messageEncryptStatusContentJson) => {
    let waitingIndex = messageEncryptStatusContentJson.waiting_index;
    let session = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
      message.senderId,
      message.senderDeviceId,
      message.groupId,
      messageEncryptStatusContentJson.encrypt_version,
    );

    if (waitingIndex > (session.error_index || 0)) {
      let updateValues = {
        retry_error: 0,
        state: 0,
        init_f: 0,
      };
      if (waitingIndex > (session.success_index || 0)) updateValues.success_index = waitingIndex;
      await getInteractor(prefixKey).LocalCryptoService.updateSession(
        updateValues,
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
      );
    }

    return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
  };

  /**
   *
   * @param message
   * @param {string} prefixKey
   * @param messageEncryptStatusContentJson
   * @returns {Promise<{type: string, message: string}>}
   */
  processGroupSuccessEncryptStatus = async (message, prefixKey, messageEncryptStatusContentJson) => {
    let waitingIndex = messageEncryptStatusContentJson.waiting_index;
    let senderKey = toCamel(
      await getInteractor(prefixKey).LocalCryptoService.getSenderKeyEntity(
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
      ),
    );
    if (waitingIndex > (senderKey.errorIndex || 0)) {
      let updateValues = {
        retry_error: 0,
        state: 0,
        init_f: 0,
      };
      if (waitingIndex > (senderKey.successIndex || 0)) updateValues.success_index = waitingIndex;
      await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
        updateValues,
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        messageEncryptStatusContentJson.encrypt_version,
      );
    }
    return { type: SystemConstant.MESSAGE_ERROR_TYPE.NONE, message: "OK" };
  };

  sendToOneDeviceMessage = async (message, msgEncrypt, group, indexNumber, prefixKey) => {
    if (!message) {
      throw new Error("Message is null");
    }
    const messageResends = await getInteractor(prefixKey).LocalMessageService.find({
      source_id: msgEncrypt?.source_id,
    });
    if (messageResends.length === 0) return;
    const messageResend = messageResends[0];
    if (messageResend?.status === SystemConstant.MESSAGE_STATUS.failed) {
      const updateMessage = {
        status: SystemConstant.MESSAGE_STATUS.send,
      };
      await getInteractor(prefixKey).LocalMessageService.updateMessage(messageResend.id, updateMessage);
    }

    const messageEncryptOption = convertString2JSON(msgEncrypt.options, {});
    const messageId = uuid();

    const resendMessage = {
      messageId: messageId,
      sendToDeviceId: message.senderDeviceId,
      sendToAccountId: message.senderId,
      content: msgEncrypt.content,
      options: JSON.stringify({ ...messageEncryptOption, index_send_number: indexNumber, is_resend: true }),
      status: SystemConstant.MESSAGE_STATUS.send,
      sendType: messageResend?.send_type,
      mentions: messageResend?.mentions,
      encryptType: SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION,
    };

    const newMessage = {
      isSendingKey: false,
      groupId: messageResend.group_id,
      sourceId: msgEncrypt.source_id,
      groupType: group.groupType,
      sendType: messageResend.send_type,
      created: Date.now(),
      messages: [resendMessage],
      branchId: messageResend.branch_id,
      parentId: messageResend.parent_id,
      threadId: messageResend.thread_id,
      deviceList: [message.senderDeviceId],
      messageId: messageId,
    };
    await getInteractor(prefixKey).LocalApiCallService.save([
      {
        id: uuid(),
        task: `${AppConstant.API_CALL_TASK.message}`,
        original_uid: newMessage.sourceId,
        query: "",
        content: JSON.stringify({ data: newMessage }),
        original_content: JSON.stringify({ data: newMessage }),
        created: new Date().getTime(),
        retry: 0,
        branch_id: newMessage.branchId,
        group_id: newMessage.groupId,
      },
    ]);
    store.dispatch(SystemActions.executeQueue());
    return null;
  };

  resendOneOnOne = async ({
    encryptStatusMes,
    resendMessages,
    sendWaitingIndex,
    prefixKey,
    encryptTypeProtocol,
    isResendE2E,
  }) => {
    const semaphore = getSemaphore(prefixKey);
    const [_value, release] = await semaphore.acquire();
    try {
      const resendMessage = resendMessages.length > 0 ? resendMessages[resendMessages.length - 1] : null;
      if (resendMessage == null) {
        return;
      }
      resendMessage.id = uuid();
      resendMessage.source_id = uuid();
      resendMessage.status = SystemConstant.MESSAGE_STATUS.send;
      resendMessage.send_type = SystemConstant.SEND_TYPE.keyError;
      resendMessage.content = { messages: resendMessages };
      resendMessage.parent_id = null;
      resendMessage.options = null;
      resendMessage.modified = new Date().getTime();
      resendMessage.created = new Date().getTime();
      resendMessage.thread_id = null;

      if (!isResendE2E) {
        await getInteractor(prefixKey).LocalCryptoService.deleteSession(
          encryptStatusMes.senderId,
          encryptStatusMes.senderDeviceId,
          encryptStatusMes.groupId,
          encryptTypeProtocol,
        );
      }
      if (resendMessage) {
        const options = convertString2JSON(resendMessage.options, {});
        const jsonOption = {
          original_send_type: options?.original_send_type ?? resendMessage.send_type,
          encrypt_version: encryptTypeProtocol,
          disappearing_f: false,
          is_resend: true,
          is_resend_e2e: isResendE2E,
        };
        jsonOption.encryption_f = 1;
        const id = uuid();
        const cipherObject = await getInteractor(prefixKey).LocalCryptoService.encryptE2EMessage(
          resendMessage.send_to_account_id,
          resendMessage.send_to_device_id,
          resendMessage.group_id,
          resendMessage.content,
          encryptTypeProtocol,
          sendWaitingIndex,
        );
        // const text = content;
        if (!cipherObject?.cipherText) return null;
        const sessionEntity = await getInteractor(prefixKey).LocalCryptoService.getSessionEntity(
          //message_encrypt data me.send_to_account_id
          resendMessage.send_to_account_id,
          resendMessage.send_to_device_id,
          resendMessage.group_id,
          encryptTypeProtocol,
        );
        let prevIndexNumber = sessionEntity.prev_index;
        jsonOption.index_send_number = cipherObject.indexSendNumber;
        jsonOption.prev_index_number = prevIndexNumber;
        const resendMessage = {
          messageId: id,
          sendToDeviceId: resendMessage.send_to_device_id,
          sendToAccountId: resendMessage.send_to_account_id,
          content: cipherObject.cipherText,
          options: JSON.stringify(jsonOption),
          status: SystemConstant.MESSAGE_STATUS.send,
          sendType: resendMessage.send_type,
          mentions: resendMessage.mentions,
          encryption_type: SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION,
        };
        const memberIdArr = await getInteractor(prefixKey).LocalAccountGroupService.find({
          group_id: resendMessage.group_id,
          state: 1,
        });
        let deviceList = null;
        if (memberIdArr && memberIdArr.length > 0) {
          deviceList = await getInteractor(prefixKey).LocalDeviceService.find({
            account_id: memberIdArr.map(member => member.account_id),
            state: SystemConstant.STATE.active,
            id: resendMessage.sender_device_id,
          });
        }

        let deviceListIds = [resendMessage.send_to_device_id];
        if (deviceList && deviceList.length > 0) {
          deviceListIds = deviceList.map(d => d.id);
        }
        const newMessage = {
          isSendingKey: false,
          groupId: resendMessage.group_id,
          sourceId: resendMessage.source_id,
          groupType: resendMessage.group_type,
          sendType: resendMessage.send_type,
          created: Date.now(),
          messages: [resendMessage],
          branchId: resendMessage.branch_id,
          parentId: resendMessage.parent_id,
          threadId: resendMessage.thread_id,
          deviceList: deviceListIds,
          messageId: id,
        };
        resendMessage.id = id;
        resendMessage.send_type = SystemConstant.SEND_TYPE.keyError;
        resendMessage.options = JSON.stringify(jsonOption);
        await getInteractor(prefixKey).LocalMessageService.save([resendMessage]);
        let updateValues = {
          prev_index: jsonOption.index_send_number,
        };
        await getInteractor(prefixKey).LocalCryptoService.updateSession(
          updateValues,
          resendMessage.send_to_account_id,
          resendMessage.send_to_device_id,
          resendMessage.group_id,
          encryptTypeProtocol,
        );
        await getInteractor(prefixKey).LocalApiCallService.save([
          {
            id: uuid(),
            task: `${AppConstant.API_CALL_TASK.message}`,
            original_uid: newMessage.sourceId,
            query: "",
            content: JSON.stringify({ data: newMessage }),
            original_content: JSON.stringify({ data: newMessage }),
            created: new Date().getTime(),
            retry: 0,
            branch_id: newMessage.branchId,
            group_id: newMessage.groupId,
          },
        ]);
        store.dispatch(SystemActions.executeQueue());
        return null;
      }
    } finally {
      release();
    }
    return null;
  };
}

export default EncryptStatus;
